import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, posApi, ApiError, type PosCardDto } from '../api';
import QrScanner from '../components/QrScanner';
import { usePageTitle } from '../hooks/usePageTitle';

const TOKEN_KEY = 'jamtick.posToken';
const RESULT_COLOR: Record<'ok' | 'warn' | 'error', string> = { ok: '#9fdcb2', warn: '#d9b36a', error: '#d99a9a' };

const STATUS_TEXT: Record<PosCardDto['status'], string> = {
  unsold: 'ยังไม่เปิดใช้งาน — ขายได้',
  sold: 'ขายไปแล้ว',
  void: 'ถูกยกเลิก',
};

/**
 * TICK-078 — the counter.
 *
 * Its own page and its own token role, because selling stock and admitting people are different
 * jobs. The one property the layout is built around: **the quote is a separate step from the sale.**
 * The seller scans, reads the price out loud, takes the cash, and only then presses the button — so
 * a mis-scan costs a second rather than a card.
 */
export default function StaffPos() {
  const { eventSlug } = useParams<{ eventSlug?: string }>();

  usePageTitle(eventSlug, 'จุดจำหน่าย');

  const [eventName, setEventName] = useState('');
  useEffect(() => {
    let cancelled = false;
    api.getEvent(eventSlug).then((event) => { if (!cancelled) setEventName(event.name); }).catch(() => {});
    return () => { cancelled = true; };
  }, [eventSlug]);

  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) ?? '');
  const [tokenInput, setTokenInput] = useState('');
  const [loginError, setLoginError] = useState('');
  const [input, setInput] = useState('');
  const [scanning, setScanning] = useState(true);
  const [pending, setPending] = useState<{ code: string; card: PosCardDto } | null>(null);
  const [selling, setSelling] = useState(false);
  const [result, setResult] = useState<{ kind: 'ok' | 'warn' | 'error'; text: string } | null>(null);
  const busy = useRef(false);

  const signOut = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken('');
  };

  const onAuthError = (err: unknown): boolean => {
    if (err instanceof ApiError && err.status === 401) {
      signOut();
      setLoginError('Token ไม่ถูกต้อง');
      return true;
    }
    return false;
  };

  /** Read-only. Asking what a card costs must never sell it. */
  const quote = useCallback(async (raw: string) => {
    const code = raw.trim();
    if (!code || busy.current) return;
    busy.current = true;
    setResult(null);
    try {
      const card = await posApi.lookup(token, code, eventSlug);
      if (card.status === 'unsold') {
        setPending({ code, card });
      } else {
        setPending(null);
        setResult({
          kind: card.status === 'sold' ? 'warn' : 'error',
          text: `${card.status === 'sold' ? '⚠' : '✕'} ${card.serial} — ${STATUS_TEXT[card.status]}`,
        });
      }
      setInput('');
    } catch (err) {
      if (!onAuthError(err)) {
        setResult({ kind: 'error', text: err instanceof ApiError ? `✕ ${err.message}` : '✕ ค้นหาบัตรไม่สำเร็จ' });
      }
    } finally {
      setTimeout(() => (busy.current = false), 300);
    }
  }, [token, eventSlug]);

  const sell = async () => {
    if (!pending || selling) return;
    setSelling(true);
    try {
      const res = await posApi.activate(token, pending.code, eventSlug);
      setResult({
        kind: res.alreadySold ? 'warn' : 'ok',
        text: res.alreadySold
          ? `⚠ ${res.serial} — ขายไปแล้วก่อนหน้านี้ (${res.order.code})`
          : `✓ ${res.serial} — เปิดใช้งานแล้ว · ${res.order.code}`,
      });
      setPending(null);
    } catch (err) {
      if (!onAuthError(err)) {
        setResult({ kind: 'error', text: err instanceof ApiError ? `✕ ${err.message}` : '✕ เปิดใช้งานไม่สำเร็จ' });
      }
    } finally {
      setSelling(false);
    }
  };

  const login = () => {
    const t = tokenInput.trim();
    if (!t) return;
    sessionStorage.setItem(TOKEN_KEY, t);
    setToken(t);
    setLoginError('');
  };

  if (!token) {
    return (
      <div className="page admin-login-wrap">
        <h1 style={{ fontSize: 28 }}>{eventName || 'JAMTICK'} — จุดจำหน่าย</h1>
        <p style={{ color: 'var(--muted)', fontSize: 14 }}>ใส่ POS token เพื่อเข้าใช้งาน</p>
        <div className="coupon-row" style={{ maxWidth: 420 }}>
          <input
            type="password"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && login()}
            placeholder="POS token"
          />
          <button className="btn-ghost" onClick={login}>เข้าสู่ระบบ</button>
        </div>
        {loginError && <div className="form-error">{loginError}</div>}
      </div>
    );
  }

  return (
    <div className="page shell" style={{ paddingTop: 32, paddingBottom: 32 }}>
      <div className="admin-order-head">
        <h1 style={{ fontSize: 28, margin: 0 }}>จุดจำหน่าย</h1>
        <button className="btn-link" onClick={signOut}>ออกจากระบบ</button>
      </div>

      <div className="card" style={{ maxWidth: 560 }}>
        {pending ? (
          <>
            <div className="card-step">ยืนยันการขาย</div>
            <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 11, padding: 18, marginBottom: 16 }}>
              <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'var(--mono)' }}>{pending.card.serial}</div>
              <div style={{ fontSize: 15, marginTop: 6 }}>{pending.card.ticketName || '—'}</div>
              <div style={{ fontSize: 30, fontWeight: 700, marginTop: 12 }}>
                {pending.card.price != null ? `฿${pending.card.price.toLocaleString('th-TH')}` : '—'}
              </div>
              {pending.card.seats > 1 && (
                <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>เข้างานได้ {pending.card.seats} คน</div>
              )}
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 14 }}>
              รับเงินให้เรียบร้อยก่อน แล้วจึงกดเปิดใช้งาน — บัตรที่เปิดแล้วจะนับเป็นยอดขายทันที
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button className="btn-primary" style={{ padding: '12px 22px' }} disabled={selling} onClick={sell}>
                {selling ? 'กำลังเปิดใช้งาน…' : 'รับเงินแล้ว — เปิดใช้งาน'}
              </button>
              <button className="btn-ghost" disabled={selling} onClick={() => setPending(null)}>ยกเลิก</button>
            </div>
          </>
        ) : (
          <>
            <div className="card-step">สแกนหรือพิมพ์เลขบัตร</div>
            {result && (
              <div style={{ color: RESULT_COLOR[result.kind], fontSize: 16, fontWeight: 600, marginBottom: 14 }}>
                {result.text}
              </div>
            )}
            {scanning ? (
              <>
                <QrScanner onDetected={quote} />
                <button className="btn-ghost" style={{ marginTop: 12 }} onClick={() => setScanning(false)}>
                  ปิดกล้อง
                </button>
              </>
            ) : (
              <button className="btn-primary" style={{ padding: '12px 22px' }} onClick={() => { setResult(null); setScanning(true); }}>
                📷 เปิดกล้องสแกน QR
              </button>
            )}
            <div className="coupon-row" style={{ marginTop: 16 }}>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && quote(input)}
                placeholder="MPAS-0001"
                style={{ fontFamily: 'var(--mono)' }}
              />
              <button className="btn-ghost" onClick={() => quote(input)}>ตรวจสอบ</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
